import { Avatar, Button, MachineRaroBrand, RaroBrand, cx } from '@raro/ui';
import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { NotificationsMenu } from '../components/NotificationsMenu';
import { hasRoleAtLeast, type MembershipRole } from '@raro/domain';
import { useAuth } from '../lib/auth';

const NAV: { to: string; label: string; soon?: string; end?: boolean }[] = [
  { to: '/', label: 'Hoje', end: true },
  { to: '/projetos', label: 'Projetos' },
  { to: '/parede', label: 'Parede' },
  { to: '/ver', label: 'VER' },
  { to: '/lab', label: 'LAB' },
  { to: '/network', label: 'Network' },
  { to: '/rastro', label: 'Rastro' },
  { to: '/o-raro', label: 'O Raro™' },
  { to: '/memoria', label: 'Memória' },
  { to: '/wall/controle', label: 'Wall' },
];
/** `min`: o papel mínimo na Raro para ver o item (o mesmo que a API exige nas rotas da página). */
const ADMIN: { to: string; label: string; min?: MembershipRole }[] = [
  { to: '/clientes', label: 'Clientes' },
  { to: '/pessoas', label: 'Pessoas', min: 'analyst' },
  { to: '/valor', label: 'Valor', min: 'partner' },
  { to: '/atividade', label: 'Atividade', min: 'partner' },
];

const COLLAPSE_KEY = 'machine.raro.sidebar';
/** Abreviação para a sidebar comprimida: "Hoje" → "Ho", "O Raro™" → "OR". */
const abbr = (label: string) => {
  const words = label.replace(/[™]/g, '').trim().split(/\s+/);
  return (words.length > 1 ? words.map((w) => w[0]).join('') : label.slice(0, 2)).toUpperCase();
};

export function AppShell() {
  const { me, logout } = useAuth();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* sem storage */ }
  }, [collapsed]);
  return (
    <div className={cx('rr-shell', collapsed && 'rr-shell--collapsed')}>
      <aside className="rr-sidebar">
        <div className="rr-sidebar__brand">
          <NavLink to="/" title="machine.raro">
            {collapsed ? <span className="rr-sidebar__mark">m<span className="rr-brand__accent">.</span></span> : <MachineRaroBrand />}
          </NavLink>
        </div>
        <nav className="rr-sidebar__nav" aria-label="Principal">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className="rr-nav-item" title={collapsed ? item.label : undefined}>
              <span className="rr-nav-item__label">{item.label}</span>
              <span className="rr-nav-item__abbr" aria-hidden>{abbr(item.label)}</span>
              {item.soon && <span className="rr-nav-item__soon">{item.soon}</span>}
            </NavLink>
          ))}
          <div style={{ flex: 1 }} />
          {ADMIN.filter((item) => !item.min || (me?.internalRole && hasRoleAtLeast(me.internalRole, item.min))).map((item) => (
            <NavLink key={item.to} to={item.to} className="rr-nav-item" title={collapsed ? item.label : undefined}>
              <span className="rr-nav-item__label">{item.label}</span>
              <span className="rr-nav-item__abbr" aria-hidden>{abbr(item.label)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="rr-sidebar__foot">
          {!collapsed && <RaroBrand />}
          {me && (
            <span className={cx('rr-row', collapsed && 'rr-sidebar__foot-stack')} style={{ gap: 4 }}>
              <NotificationsMenu />
              <NavLink to="/perfil" title="perfil"><Avatar name={me.name} accent /></NavLink>
              {!collapsed && <Button variant="quiet" size="sm" onClick={logout} title={`${me.email} · sair`}>sair</Button>}
            </span>
          )}
        </div>
        <button type="button" className="rr-sidebar__toggle" onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'expandir menu' : 'comprimir menu'} aria-label={collapsed ? 'expandir menu' : 'comprimir menu'} aria-expanded={!collapsed}>
          {collapsed ? '›' : '‹'}
        </button>
      </aside>
      <div className="rr-main">
        <Outlet />
      </div>
    </div>
  );
}
